import { Component, type ErrorInfo, type ReactNode } from 'react';

import { isApiError } from '@/api/errors';

interface Props {
  children: ReactNode;
  /** Changing this remounts the boundary — pass the route so navigation clears a caught error. */
  resetKey?: string;
}

interface State {
  error: Error | null;
}

/* eslint-disable i18next/no-literal-string --
   This boundary is mounted outside the i18n provider on purpose: it has to
   render even when i18n itself is what failed. Its strings are therefore
   hardcoded, and it is the only component in the application allowed to do so. */

/**
 * Catches render-time crashes so one broken screen does not blank the whole
 * application. Shows the correlation id when the failure came from the API,
 * because that is the one string support needs to find the request in the logs.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidUpdate(previous: Props): void {
    if (previous.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Error no controlado en la interfaz', error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    const requestId = isApiError(error) ? error.requestId : '';

    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 px-6 text-center">
        <h1 className="text-lg font-semibold">Algo salió mal</h1>
        <p className="text-ink-muted max-w-md text-sm">
          La pantalla no pudo mostrarse. Vuelve a intentarlo; si el problema persiste, comunica el
          código de abajo a soporte.
        </p>
        {requestId && (
          <code className="bg-surface-sunken text-ink-muted rounded px-2 py-1 text-xs">
            {requestId}
          </code>
        )}
        <button
          type="button"
          onClick={() => this.setState({ error: null })}
          className="bg-brand-900 hover:bg-brand-700 rounded-(--radius-control) px-4 py-2 text-sm font-medium text-white"
        >
          Reintentar
        </button>
      </div>
    );
  }
}
/* eslint-enable i18next/no-literal-string */
